import { useEffect } from 'react';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper';
import ButtonLink from '../shared/ButtonLinkComponent';
import { withWidgetSpacingWrapper } from '../shared/WidgetSpacingWrapper';
import WidgetIdWrapper from '../shared/WidgetIdWrapper';
import SvgFromFilePath from '../shared/SvgFromFilePath';
import { device } from '../../utils/styled-component-mixins';
import useMediaQuery from '../custom-hooks/useMediaQuery';
import { useLocalization } from '../custom-hooks/useLocalization';
import { withLocalization } from '../shared/Context/LocalizationContext';
import background from '../assets/images/angled-background.png';
import ServerStylesConditionalWrapper from '../shared/ServerStylesConditionalWrapper';
import 'swiper/css';
import 'swiper/css/pagination';
import { compose } from '../utils/compose';

const BusinessSolutionsWidgetWrapper = styled.div`
  text-align: center;

  .business-solution-card-row {
    justify-content: center;
  }
  .background-wrapper {
    ${(props) => props.backgroundImage && `background-image: url("${props.backgroundImage}");`}

    @media ${device.desktop} {
      padding-bottom: 12rem;
    }
  }

  .business-solution-widget-heading {
    font-size: 3rem;
    font-weight: 400;
    line-height: 3.2rem;
    margin-bottom: 1.6rem;
    max-width: 75%;

    @media ${device.desktop} {
      font-size: 3.6rem;
      font-weight: 400;
      line-height: 4.8rem;
      max-width: 65rem;
    }
  }

  .business-solution-widget-summary {
    font-size: 1.8rem;
    line-height: 3.2rem;
    margin-bottom: 4rem;
    max-width: 65rem;

    p {
      line-height: 2.4rem;
      margin-top: 0;
      @media ${device.desktop} {
        line-height: 3.2rem;
      }
    }

    @media ${device.desktop} {
      font-size: 2rem;
    }
  }

  .business-solution-widget-heading,
  .business-solution-widget-summary {
    margin-left: auto;
    margin-right: auto;
  }
`;

const FeaturedBusinessSolutionsWrapper = styled.div`
  ${(props) => `background-image: url("${props.absoluteUrl}/dist/${background}");`}
  background-repeat: no-repeat;
  background-size: cover;
  background-position: 75% center;
  box-shadow: 0px 16px 80px rgba(91, 110, 127, 0.25);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  overflow: hidden;
  padding: 3.2rem 2.4rem 8rem 2.4rem;
  position: relative;
  text-align: left;

  @media ${device.desktop} {
    padding: 8rem 6.4rem 6.4rem;
  }

  & > [class^='WidgetIdWrapper'] {
    height: auto;
  }

  &:after {
    content: '';
    background: linear-gradient(360deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.8) 92.71%);
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
  }

  h3 {
    font-size: 2rem;
    font-weight: 800;
    line-height: 2.4rem;
    margin-bottom: 1.6rem;

    @media ${device.desktop} {
      font-size: 2.2rem;
      line-height: 3.2rem;
    }
  }

  p {
    font-size: 1.4rem;
    line-height: 2.4rem;
  }

  .svg-wrapper {
    position: absolute;
    top: -5%;
    right: -10%;
    width: 48%;
    z-index: 2;

    svg {
      fill: var(--charcoal);
      stroke: var(--charcoal);
      opacity: 0.05;
    }
  }

  .btn-wrapper,
  .business-solution-card-content {
    position: relative;
    z-index: 3;
  }

  .btn-wrapper {
    margin-top: 3.2rem;
    justify-content: center;

    @media ${device.desktop} {
      margin-top: 15rem;
    }

    .btn {
      font-size: 1.4rem;
      line-height: 1.8rem;
      padding: 0.8rem 3rem;
      text-align: center;
      width: 100%;

      @media ${device.desktop} {
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }
  }
`;

const Solutions = ({ item }) => {
  const { resourceStrings } = useLocalization();
  let urlOrigin = '';

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    urlOrigin = origin;
  }, []);

  return (
    <FeaturedBusinessSolutionsWrapper absoluteUrl={urlOrigin}>
      <SvgFromFilePath path={item.icon} />
      <div className="business-solution-card-content">
        <h3>{item.pageTitle}</h3>
        <p>{item.description}</p>
      </div>
      <ButtonLink
        text={resourceStrings['bdO.FeaturedBusinessSolutions.CardButtonText']}
        link={item.url}
        target="_self"
        type="primary"
        theme="light"
      />
    </FeaturedBusinessSolutionsWrapper>
  );
};

const FeaturedBusinessSolutions = (props) => {
  const { heading, summary, backgroundImage, widgetID } = props.properties;
  const isDesktop = useMediaQuery(device.desktop);

  if (isDesktop) {
    return (
      <WidgetIdWrapper widgetID={widgetID}>
        <BusinessSolutionsWidgetWrapper backgroundImage={backgroundImage}>
          <div className="container">
            <h2 className="business-solution-widget-heading">{heading}</h2>
          </div>
          <div className="background-wrapper angled-background">
            <div className="container">
              <div
                className="business-solution-widget-summary"
                dangerouslySetInnerHTML={{ __html: summary }}
              ></div>
              <div className="row business-solution-card-row">
                {props.items.map((item, index) => (
                  <div className="col-lg-4" key={index}>
                    <Solutions item={item} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </BusinessSolutionsWidgetWrapper>
      </WidgetIdWrapper>
    );
  }

  return (
    <ServerStylesConditionalWrapper>
      <BusinessSolutionsWidgetWrapper className="card-slider" backgroundImage={backgroundImage}>
        <div className="container">
          <h2 className="business-solution-widget-heading">{heading}</h2>
          <div
            className="business-solution-widget-summary"
            dangerouslySetInnerHTML={{ __html: summary }}
          ></div>
        </div>
        <div className="background-wrapper angled-background">
          <div className="mobile-slider-row">
            <Swiper
              pagination={{ clickable: false }}
              centeredSlides={true}
              slidesPerView="auto"
              spaceBetween={16}
              modules={[Pagination]}
              init={false}
            >
              {props.items.map((item, index) => (
                <SwiperSlide key={index}>
                  <Solutions item={item} />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </BusinessSolutionsWidgetWrapper>
    </ServerStylesConditionalWrapper>
  );
};

export default compose(withLocalization, withWidgetSpacingWrapper)(FeaturedBusinessSolutions);
